import { type ReactElement, type MouseEvent, Children, cloneElement } from 'react';
import { EventType } from 'common/EventConstants';
import { useApi } from 'client/lib/trpc';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { useDeploymentStatusTree } from 'client/state/useDeploymentStatusTree';
import { useDeploymentWarning } from 'client/pages/deployment/useDeploymentWarning';
import { DropdownMenu } from 'client/components/DropdownMenu';
import { useViewAppBlocker } from './useViewAppBlocker';

interface ViewAppBlockerProps {
  host: {
    stackComponentID: number;
    identifier: string;
    defaultDomain: string | null;
    tweakedDomain: string | null;
    customDomain: string | null;
  };
  children: ReactElement;
}

export const ViewAppBlocker = ({ host, children }: ViewAppBlockerProps) => {
  const api = useApi();
  const deploymentID = useDeploymentClientState((state) => state.document?.id);
  const statusTree = useDeploymentStatusTree(deploymentID);

  const id = `${host.stackComponentID}-${host.identifier}`;
  const href = `https://${host.customDomain || host.tweakedDomain || host.defaultDomain || ''}`;

  const containers = (statusTree?.containers.items || []).filter((item) => item.stackComponentID === host.stackComponentID);

  const { menuItems } = useViewAppBlocker({ deploymentID, host: id, href });
  const { isWarningDisplayed } = useDeploymentWarning({ containers });

  const handlePreventOpen = (event: MouseEvent<HTMLButtonElement>) => {
    if (deploymentID && isWarningDisplayed) {
      if (event) event.preventDefault();
      api.status.eventCreate.mutate({
        eventTypeID: EventType.DEPLOYMENT_HOST_OPEN_INACTIVE,
        deploymentID,
        host: id,
        reason: 'starting',
      });
    }
  };

  if (isWarningDisplayed) {
    const child = Children.only(children);
    const button = cloneElement(child, {
      ...child.props,
      onClick: handlePreventOpen,
    });

    return <DropdownMenu items={menuItems}>{button}</DropdownMenu>;
  }

  return children;
};
